import {
  AssetLocationSource,
  EntityRefUtil,
  formatDateWithUserTimezone,
  formatTimeWithUserTimezone,
  FormType,
  GetAssetLocationsRequestDTO,
  GetAssetLocationsResponseDTO,
} from '@gearbelt/global';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { match } from 'ts-pattern';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { formatRateCellValue } from '@/utils/currency';

const columnHelper = createColumnHelper<GetAssetLocationsResponseDTO['data'][number]>();

export const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <div className="flex w-full justify-center">
        <label className="-m-2 p-2">
          <Checkbox checked={table.getIsAllPageRowsSelected()} onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)} aria-label="Select all" />
        </label>
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex w-full justify-center">
        <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
        </label>
      </div>
    ),
    meta: {
      cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
    },
    enableSorting: false,
    enableHiding: false,
  }),
  columnHelper.accessor('recordedAt', {
    header: 'Created Date',
    cell: ({ row }) => {
      const recordedAt = row.original.recordedAt;
      const userTimezone = useUserStore.getState().user?.timezone;
      return <span>{formatDateWithUserTimezone(new Date(recordedAt), userTimezone)}</span>;
    },
    enableSorting: true,
  }),
  columnHelper.display({
    id: 'createdTime',
    header: 'Created Time',
    cell: ({ row }) => {
      const recordedAt = row.original.recordedAt;
      const userTimezone = useUserStore.getState().user?.timezone;
      return <span>{formatTimeWithUserTimezone(new Date(recordedAt), userTimezone)}</span>;
    },
    enableSorting: true,
  }),
  columnHelper.accessor('location', {
    header: 'Location',
    cell: ({ getValue }) => {
      const location = getValue();
      return <span className="block truncate">{location?.name}</span>;
    },
    enableSorting: true,
    enableGrouping: true,
    getGroupingValue: (row) => row.location?.name || 'None',
    enableColumnFilter: true,
    meta: {
      cellClassName: 'max-w-[200px] overflow-hidden',
      filterType: 'async-multi-select',
      getFilterOptionsFn: async (inputValue, value) => {
        const filters = inputValue ? { filters: { search: inputValue } } : undefined;
        const { data } = await (await ApiService.GetLocations({ limit: 500, ...filters })).json();

        const resultsMap = new Map(data.map((location) => [location.id, { label: location.name, value: location.id }]));

        if (value?.length) {
          const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
          if (missingIds.length) {
            const { data: missingLocations } = await (
              await ApiService.GetLocations({
                limit: 500,
                filters: { fields: { id: missingIds } },
              })
            ).json();

            for (const location of missingLocations) {
              resultsMap.set(location.id, { label: location.name, value: location.id });
            }
          }
        }

        return Array.from(resultsMap.values());
      },
    },
  }),
  columnHelper.accessor('lat', {
    header: 'Latitude',
    cell: ({ getValue }) => {
      const lat = getValue();

      return <span>{lat}</span>;
    },
    enableSorting: false,
  }),
  columnHelper.accessor('lng', {
    header: 'Longitude',
    cell: ({ getValue }) => {
      const lng = getValue();
      return <span>{lng}</span>;
    },
    enableSorting: false,
  }),
  columnHelper.accessor('rate', {
    header: 'Rate',
    cell: ({ getValue }) => {
      const value = getValue();
      const formatted = formatRateCellValue(value);
      if (!formatted) return <span className="text-secondary-text">-</span>;
      return <span>{formatted}</span>;
    },
    enableSorting: true,
  }),
  columnHelper.accessor('source', {
    header: 'Source',
    cell: ({ getValue, row }) => {
      const source = getValue();
      const form = row.original.form;

      return (
        <Badge
          variant={match(source as AssetLocationSource)
            .with(AssetLocationSource.Manual, () => 'success' as const)
            .with(AssetLocationSource.Form, () => {
              if (!form) return 'default' as const;
              return match(form.type)
                .with(FormType.InspectionForm, () => 'default' as const)
                .with(FormType.RepairForm, () => 'orange' as const)
                .with(FormType.ScheduleForm, () => 'violet' as const)
                .otherwise(() => 'default' as const);
            })
            .exhaustive()}
          className="max-w-[150px] capitalize">
          <span className="truncate">
            {match(source as AssetLocationSource)
              .with(AssetLocationSource.Manual, () => 'Manual' as const)
              .with(AssetLocationSource.Form, () => form?.name || ('Form' as const))
              .exhaustive()}
          </span>
        </Badge>
      );
    },
    enableGrouping: true,
    getGroupingValue: (row) => (row.source === AssetLocationSource.Manual ? 'Manual' : 'Form'),
  }),
  columnHelper.display({
    id: 'createdBy',
    header: 'Created By',
    cell: ({ row }) => {
      const user = row.original.user;
      if (!user) return <span className="text-secondary-text">-</span>;

      const value = EntityRefUtil.createUserFullNameRef(user, '-');
      const avatarBackgroundColorClass = getAvatarBackgroundColorClass(user?.firstName || '');
      return (
        <UserAvatarLabel
          value={value || '-'}
          operatedByFullInitials={initials(value || '-')}
          avatarUrl={user?.avatarUrl || ''}
          avatarBackgroundColorClass={avatarBackgroundColorClass}
        />
      );
    },
    meta: {
      cellClassName: 'w-[120px]',
    },
    enableSorting: false,
    enableGrouping: true,
    getGroupingValue: (row) => {
      return EntityRefUtil.createUserFullNameRef(row.user, 'None');
    },
  }),
] as AccessorKeyColumnDef<GetAssetLocationsResponseDTO['data'][number]>[];

export const FILTER_FIELD_MAP: Record<
  NonNullable<NonNullable<typeof columns>[number]['id']> | NonNullable<NonNullable<typeof columns>[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetAssetLocationsRequestDTO['filters']>['fields']>
> = {
  location: 'locationId',
  source: 'source',
};

export const GROUP_OPTIONS = [
  { label: 'None', accessor: '' },
  { label: 'Location', accessor: 'location' },
  { label: 'Source', accessor: 'source' },
  { label: 'Created By', accessor: 'createdBy' },
] satisfies { label: string; accessor: string }[];
